import { YES_NO_OPTIONS,
  getExitStrategies, getLoanPurposes, getPropertyTypes, getRefinanceLoanTypes, 
  getRealEstateExpPropertyTypes, getRealEstateExpProjectStatus, getRealEstateExpLoanTypes
} from "../../(common-lib)/PickList";
import { ApplicationSection, SectionGroup } from "./ApplicationTypes";

const YES_NO_OPTIONS_SIMPLE = Object.values(YES_NO_OPTIONS).map((opt) => opt.display);
const PROJECT_MANAGER_OPTIONS = ['Myself (Not a Licensed Contractor)', 
                                  'Myself (Licensed Contractor)', 
                                  '3rd-Party Licensed Contractor'];
const ENTITY_TYPE_OPTIONS = [
  'LLC - Member Managed',
  'LLC - Manager Managed',
  'Corporation',
  'Limited Partnership',
  'Revocable Trust'
];

const LIQUIDITY_ACCOUNT_TYPE_OPTIONS = [
  'Checking',
  'Savings',
  'Brokerage',
  '401(k) or IRA',
  'HELOC'
];

// Static application sections definition only
export const applicationSections: (ApplicationSection | SectionGroup)[] = [
  { // general_information
    id: 'general_information',
    title: 'General Information',
    description: 'TODO',
    isLevel1Section: true,
    sections: [
      { // loan_details
        id: 'loan_details',
        title: 'Loan Details',
        description: 'Provide the key terms of your financing request so we can properly structure your loan.',
        group: 'general_information',
        fields: [
          { id: 'loan_purpose', label: 'Loan Purpose', type: 'select', required: true, value: 'Purchase', options: getLoanPurposes() }, 
          { id: 'loan_type', label: 'Loan Type', type: 'select', required: true, value: 'Bridge', options: getExitStrategies() }, 
          { id: 'purchase_price', label: 'Purchase Price', type: 'currency', required: true, helpText: 'To fill out' },
          { id: 'original_purchase_date', label: 'Original Purchase Date', type: 'date', required: true, helpText: 'Date when the property was originally purchased' },
          { id: 'rehab_costs_spent', label: 'Rehab Costs Spent to Date', type: 'currency', required: false, helpText: 'Amount already spent on rehabilitation work'},
          { id: 'as_is_value', label: 'As-Is Value', type: 'currency', required: true, helpText: 'Current market value of the property in its present condition' },
          { id: 'existing_debt', label: 'Existing Debt', type: 'currency', required: true, helpText: 'Any existing debt on the property' },
          { id: 'refinance_loan_type', label: 'What type of loan is being refinanced?', type: 'select', required: true, options: getRefinanceLoanTypes() },
          { id: 'existing_loan_good_standing', label: 'Is the existing loan in good standing?', type: 'select', required: true, options: ['Yes', 'No'] },
          { id: 'rehab_budget', label: 'Rehab Budget', type: 'currency', required: true, helpText: 'Total budget allocated for rehabilitation/renovation work' },
          { id: 'arv', label: 'ARV (After Repair Value)', type: 'currency', required: true, helpText: 'Estimated value of the property after completion of rehabilitation' },
          { id: 'seller_concessions', label: 'Seller Concessions', type: 'currency', required: false},
          { id: 'assignment_fee', label: 'Assignment Fee', type: 'currency', required: false} 
        ],
        documents: [] // No required documents for loan_details
      },
      { // property_details
        id: 'property_details',
        title: 'Property Details',
        description: 'Enter the essential details of the property securing the loan.',
        group: 'general_information',
        fields: [
          { id: 'property_address_line1', label: 'Address Line 1', type: 'text', required: true, value: '123 Main Street', helpText: 'Street address' },
          { id: 'property_address_line2', label: 'Address Line 2', type: 'text', required: false, helpText: 'Apartment, suite, unit, building, floor, etc.', },
          { id: 'property_city', label: 'City', type: 'text', required: true, value: 'Austin', helpText: 'City name', },
          { id: 'property_state', label: 'State', type: 'select', required: true, value: '', options: [], additional_info: 'state', helpText: 'Select the state', },
          { id: 'property_zip', label: 'ZIP Code', type: 'text', required: true, value: '78701', helpText: 'ZIP or postal code', },
          { id: 'property_type', label: 'Property Type', type: 'select', required: true, value: 'Single Family Home', options: getPropertyTypes() },
          { id: 'residential_unit_count', label: 'Residential Unit Count', type: 'integer', required: true, helpText: 'Number of residential units in the property' },
          { id: 'commercial_unit_count', label: 'Commercial Unit Count', type: 'integer', required: true, helpText: 'Number of commercial units (if applicable)' },
          { id: 'tenant_occupied', label: 'Is the property currently tenant-occupied?', type: 'select', required: true, options: ['Yes', 'No'] },
          { id: 'accepted_purchase_contract', label: 'Do you have an accepted purchase contract?', type: 'select', required: true, options: ['Yes', 'No'] }
        ],
        documents: []
      },  
      { // exit_strategy_details
        id: 'exit_strategy_details',
        title: 'Exit Strategy Details',
        description: 'Clarify your business plan and expected exit strategy for the property.',
        group: 'general_information',
        fields: [
          { id: 'days_to_finish_rehab_project', label: 'Estimated number of days to finish your rehab project', type: 'integer', required: true },
          { id: 'detailed_exit_strategy', label: 'Detailed Exit Strategy', type: 'select', required: true, options: [], value: 'No' },
        ],
      },
      { // real_estate_experience
        id: 'real_estate_experience',
        title: 'Real Estate Investment Experience',
        description: 'Provide details for prior investment properties where you were listed on title as an owner or borrower, including property type, location, acquisition date, loan amount, exit date (if applicable), and outcome. This experience is reviewed during underwriting to assess your track record and may impact loan terms.',
        group: 'general_information',
        fields: [
          { id: 'real_estate_experience_property_address_line1', label: 'Address Line 1', type: 'text', required: false },
          { id: 'real_estate_experience_property_address_line2', label: 'Address Line 2', type: 'text', required: false },
          { id: 'real_estate_experience_property_city', label: 'City', type: 'text', required: false },
          { id: 'real_estate_experience_property_state', label: 'State', type: 'select', required: false, options: [], additional_info: 'state' },
          { id: 'real_estate_experience_property_zip', label: 'ZIP Code', type: 'text', required: false },
          { id: 'real_estate_experience_ownership_entity_on_title', label: 'Ownership Entity on Title', type: 'text', required: false },
          { id: 'real_estate_experience_property_type', label: 'Property Type', type: 'select', required: false, options: getRealEstateExpPropertyTypes() },
          { id: 'real_estate_experience_project_type', label: 'Project Type', type: 'select', required: false, options: getExitStrategies() }, 
          { id: 'real_estate_experience_purchase_price', label: 'Original Purchase Price', type: 'currency', required: false },
          { id: 'real_estate_experience_rehab_cost', label: 'Rehab Cost', type: 'currency', required: false },
          { id: 'real_estate_experience_project_status', label: 'Project Status', type: 'select', required: false, options: getRealEstateExpProjectStatus() },
          { id: 'real_estate_experience_arv', label: 'After Repair Value (ARV) or Sale Price', type: 'currency', required: false },
           { id: 'real_estate_experience_loan_type', label: 'Type of loan used for this project', type: 'select', required: false, options: getRealEstateExpLoanTypes() },
        ],
        documents: []
      },
    ]
  },
  { // property_information
    id: 'property_information',
    title: 'Property Information',
    description: 'TODO',
    isLevel1Section: true,
    sections: [
      { // property_contact_information
        id: 'property_contact_information',
        title: 'Property Contact Information',
        description: 'Provide the best contact for inspections, appraisals, or site visits.',
        group: 'property_information',
        displayOrder: 1,
        fields: [
          { id: 'property_contact_first_name', label: 'Contact First Name', type: 'text', required: true },
          { id: 'property_contact_last_name', label: 'Contact Last Name', type: 'text', required: true },
          { id: 'property_contact_phone', label: 'Phone Number', type: 'phone', required: true },
          { id: 'property_contact_email', label: 'Email Address', type: 'email', required: true },
        ],
        documents: []
      },
      { // upload_signed_purchase_contract
        id: 'upload_signed_purchase_contract',
        title: 'Upload Signed Purchase Contract',
        description: 'Please upload the fully executed purchase contract that matches the exact property address and buyer name listed on your loan application. Ensure all pages are included and that all required signatures and initials appear in the designated areas, along with any addendums or amendments.',
        group: 'property_information',
        displayOrder: 2,
        fields: [],
        documents: [
          { id: 'purchase_contract_doc', name: 'Upload your Purchase Contract', required: true, description: '' },
        ]
      },
      { // upload_signed_contract_assignment
        id: 'upload_signed_contract_assignment',
        title: 'Upload Signed Contract Assignment',
        description: 'Please upload the fully executed assignment agreement if contract rights have been transferred to you. The agreement must clearly reflect the subject property address and assigned buyer.',
        group: 'property_information',
        displayOrder: 3,
        fields: [],
        documents: [
          { id: 'contract_assignment_doc', name: 'Upload your Contract Assignment', required: true, description: '' },
        ]
      },
      { // upload_signed_contract_assignment
        id: 'upload_mortgage_statement',
        title: 'Upload Mortgage Statement',
        description: 'Please upload the most recent mortgage statement or payoff letter for the subject property, showing the current balance, loan terms, and payment status.',
        group: 'property_information',
        displayOrder: 4,
        fields: [],
        documents: [
          { id: 'mortgage_statement_doc', name: 'Upload your Mortgage Statement', required: true, description: '' },
        ]
      },
      { // upload_property_comparables
        id: 'upload_property_comparables',
        title: 'Upload Property Comparables (optional)',
        description: 'Optionally submit recent comparable sales (preferably within 6 months and within 1 mile of the subject property) if you would like them considered during valuation review. Include address, sale price, sale date, and brief notes on condition or renovations.',
        group: 'property_information',
        displayOrder: 5,
        fields: [],
        documents: [
          { id: 'property_comparables_doc', name: 'Upload Property Comparables (optional)', required: false, description: '' },
        ]
      },
      { // upload_rent_roll_opex
        id: 'upload_rent_roll_opex',
        title: 'Upload Rent Roll & OpEx Schedule',
        description: 'Provide a current rent roll and operating expense schedule, including unit mix, lease start/end dates, monthly rent, security deposits, and vacancy status. Include trailing 12-month operating expenses and pro-forma projections (if applicable).',
        group: 'property_information',
        displayOrder: 6,
        fields: [],
        documents: [
          { id: 'rent_roll_opex_doc', name: 'Upload Rent Roll & OpEx Schedule', required: true, description: '' },
        ]
      },
      { // upload_in_place_leases
        id: 'upload_in_place_leases',
        title: 'Upload In-Place Leases',
        description: 'Provide copies of current tenant leases for all occupied units, including lease start/end dates, monthly rent, security deposit details, and renewal terms.',
        group: 'property_information',
        displayOrder: 7,
        fields: [],
        documents: [
          { id: 'in_place_leases_doc', name: 'Upload In-Place Leases', required: true, description: '' },
        ]
      },
    ]
  },
  { // project_information
    id: 'project_information',
    title: 'Project Information',
    description: 'TODO',
    isLevel1Section: true,
    sections: [
      { // project_management
        id: 'project_management',
        title: 'Project Management',
        description: 'Please provide details about who will be managing the project. A licensed general contractor is required for foundation work, adding square footage, structural modifications, or any major repairs due to fire, flooding, or other significant damage.',
        group: 'project_information',
        displayOrder: 1,
        fields: [
          { id: 'project_manager', label: 'Who will be managing the project?', type: 'select', required: true, options: PROJECT_MANAGER_OPTIONS }, 
          { id: 'additional_comments', label: 'Additional Comments', type: 'text', required: false },
          { id: 'general_contractor_first_name', label: 'General Contractors First Name', type: 'text', required: false },
          { id: 'general_contractor_last_name', label: 'General Contractors Last Name', type: 'text', required: false },
          { id: 'general_contractor_phone', label: 'General Contractors Phone Number', type: 'phone', required: false },
          { id: 'license_state_or_city', label: 'License State or City', type: 'text', required: false },
          { id: 'license_number', label: 'License Number', type: 'text', required: false }
        ],
        documents: [ ]
      },
      { // project_summary
        id: 'project_summary',
        title: 'Project Summary',
        description: 'Provide a high-level overview of the renovation plan, target buyer or tenant profile, finish level (standard, mid-grade, luxury), layout changes, square footage additions, and overall repositioning strategy. This summary helps determine the accuracy of the projected After-Repair Value.',
        group: 'project_information',
        displayOrder: 2,
        fields: [
          { id: 'project_heading_details', label: 'Project Details', type: 'heading', required: false },
          { id: 'project_description', label: 'Project Description', type: 'textarea', required: true, helpText: 'Scope of work, finishes, conversions, square footage adds, and redesigns' },
          { id: 'project_target_finish_quality', label: 'Target Quality of Finishes', type: 'select', options: ['Low Range', 'Mid Range', 'Luxury'], required: true },
          { id: 'project_occupied_at_closing', label: 'Will the property be occupied at closing?', type: 'select', options: ['Yes', 'No'], required: true },

          // Above ground - current
          { id: 'project_heading_above', label: 'Above Ground Information', type: 'heading', required: false },
          { id: 'project_above_current_sqft', label: 'Above Ground (Current) - Est. Sq. Ft.', type: 'integer', required: true },
          { id: 'project_above_current_property_type', label: 'Above Ground (Current) - Property Type', type: 'select', options: getPropertyTypes(), required: true },
          { id: 'project_above_current_bedrooms', label: 'Above Ground (Current) - No. of Bedrooms', type: 'integer', required: true },
          { id: 'project_above_current_bathrooms', label: 'Above Ground (Current) - No. of Bathrooms', type: 'integer', required: true },

          // Above ground - rehabbed
          { id: 'project_heading_above_rehabbed', label: 'Above Ground (Rehabbed)', type: 'heading', required: false },
          { id: 'project_above_rehab_sqft', label: 'Above Ground (Rehabbed) - Est. Sq. Ft.', type: 'integer', required: true },
          { id: 'project_above_rehab_property_type', label: 'Above Ground (Rehabbed) - Property Type', type: 'select', options: getPropertyTypes(), required: true },
          { id: 'project_above_rehab_bedrooms', label: 'Above Ground (Rehabbed) - No. of Bedrooms', type: 'integer', required: true },
          { id: 'project_above_rehab_bathrooms', label: 'Above Ground (Rehabbed) - No. of Bathrooms', type: 'integer', required: true },

          // Below ground - current
          { id: 'project_heading_below', label: 'Below Ground Information', type: 'heading', required: false },
          { id: 'project_below_current_sqft', label: 'Below Ground (Current) - Est. Sq. Ft.', type: 'integer', required: true },
          { id: 'project_below_current_living_rooms', label: 'Below Ground (Current) - No. of Living Rooms', type: 'integer', required: true },
          { id: 'project_below_current_bedrooms', label: 'Below Ground (Current) - No. of Bedrooms', type: 'integer', required: true },
          { id: 'project_below_current_bathrooms', label: 'Below Ground (Current) - No. of Bathrooms', type: 'integer', required: true },

          // Below ground - rehabbed
          { id: 'project_heading_below_rehabbed', label: 'Below Ground (Rehabbed)', type: 'heading', required: false },
          { id: 'project_below_rehab_sqft', label: 'Below Ground (Rehabbed) - Est. Sq. Ft.', type: 'integer', required: true },
          { id: 'project_below_rehab_living_rooms', label: 'Below Ground (Rehabbed) - No. of Living Rooms', type: 'integer', required: true },
          { id: 'project_below_rehab_bedrooms', label: 'Below Ground (Rehabbed) - No. of Bedrooms', type: 'integer', required: true },
          { id: 'project_below_rehab_bathrooms', label: 'Below Ground (Rehabbed) - No. of Bathrooms', type: 'integer', required: true }
        ],
        documents: []
      },
      { // scope_of_work
        id: 'scope_of_work',
        title: 'Scope of Work',
        description: 'A detailed scope of work is required before your loan can be fully processed. Include all line items, quantities, and costs. All projects must include a contingency of 5% or $1,000 (whichever is greater). Incomplete scopes may delay approval or result in denied reimbursements.',
        group: 'project_information',
        displayOrder: 3,
        fields: [
          { id: 'scope_of_work', label: 'Scope of Work', type: 'text', required: true }
        ],
        documents: []
      },
      { // upload_building_plans
        id: 'upload_building_plans',
        title: 'Upload Building Plans',
        description: 'Please upload architectural, engineering, or construction plans detailing the proposed improvements, if applicable.',
        group: 'project_information',
        displayOrder: 4,
        fields: [],
        documents: [
          { id: 'building_plans_doc', name: 'Upload your Building Plans', required: true, description: '' },
        ]
      },
      { // upload_active_building_permit
        id: 'upload_active_building_permit',
        title: 'Upload Active Building Permit',
        description: 'Please upload all issued building permits for the project so we can confirm the required approvals are in place prior to funding.',
        group: 'project_information',
        displayOrder: 5,
        fields: [],
        documents: [
          { id: 'building_permit_doc', name: 'Upload your Building Permit', required: true, description: '' },
        ]
      },
    ]
  },
  { // third_party_information
    id: 'third_party_information',
    title: 'Third-Party Information',
    description: 'Insurance, title, and legal contacts',
    isLevel1Section: true,
    sections: [
      { // title_agent_information
        id: 'title_agent_information',
        title: 'Title Agent Information',
        description: 'Provide your preferred licensed title agent’s full contact details so we can coordinate title, payoffs, and closing documents directly.',
        group: 'third_party_information',
        fields: [
          { id: 'title_agent_contact_first_name', label: 'Contact First Name', type: 'text', required: true },
          { id: 'title_agent_contact_last_name', label: 'Contact Last Name', type: 'text', required: true },
          { id: 'title_agent_phone', label: 'Contact Phone Number', type: 'phone', required: true },
          { id: 'title_agent_email', label: 'Contact Email Address', type: 'email', required: true },
        ],
        documents: []
      },
      { // attorneys_information
        id: 'attorneys_information',
        title: 'Your Attorney\'s Information',
        description: 'Please provide your attorney’s full contact information. Your attorney will be required to review the loan documents and deliver a signed Attorney Opinion Letter, in form and substance acceptable to the lender, prior to closing.',
        group: 'third_party_information',
        fields: [
          { id: 'borrower_attorney_contact_first_name', label: 'Contact First Name', type: 'text', required: true },
          { id: 'borrower_attorney_contact_last_name', label: 'Contact Last Name', type: 'text', required: true },
          { id: 'borrower_attorney_phone', label: 'Contact Phone Number', type: 'phone', required: true },
          { id: 'borrower_attorney_email', label: 'Contact Email Address', type: 'email', required: true }
        ]
      },
      { // insurance_agent_information
        id: 'insurance_agent_information',
        title: 'Hazard Insurance Agent Information',
        description: 'Provide your insurance agent’s contact details so we can coordinate coverage requirements.',
        group: 'third_party_information',
        fields: [
          { id: 'insurance_agent_contact_first_name', label: 'Contact First Name', type: 'text', required: true },
          { id: 'insurance_agent_contact_last_name', label: 'Contact Last Name', type: 'text', required: true },
          { id: 'insurance_agent_phone', label: 'Contact Phone Number', type: 'phone', required: true },
          { id: 'insurance_agent_email', label: 'Contact Email Address', type: 'email', required: true },
        ],
        documents: []
      }
    ]
  },
  { // borrower_information
    id: 'borrower_information',
    title: 'Borrower Information',
    description: 'TODO',
    isLevel1Section: true,
    sections: [
      { // entity_information
        id: 'entity_information',
        title: 'Entity Information',
        description: 'Provide the legal name, entity type (LLC, Corporation, LP, etc.) and state of formation of the borrowing entity. Information must match your formation documents exactly.',
        group: 'borrower_information',
        fields: [
          { id: 'entity_name', label: 'Entity Name', type: 'text', required: true },
          { id: 'entity_email', label: 'Email Address', type: 'email', required: true },
          { id: 'entity_type', label: 'Entity Type', type: 'select', required: true, options: ENTITY_TYPE_OPTIONS },
          { id: 'entity_state_of_formation', label: 'State of Formation', type: 'select', required: true, additional_info: 'state' },
          { id: 'entity_ein_number', label: 'EIN Number', type: 'text', required: true },
          { id: 'entity_present_address_line1', label: 'Present Address Line 1', type: 'text', required: true },
          { id: 'entity_present_address_line2', label: 'Present Address Line 2', type: 'text', required: false },
          { id: 'entity_present_city', label: 'Present City', type: 'text', required: true },
          { id: 'entity_present_state', label: 'Present State', type: 'select', required: true, additional_info: 'state' },
          { id: 'entity_present_zip', label: 'Present ZIP Code', type: 'text', required: true },
          { id: 'entity_mailing_address', label: 'Use a different mailing address?', type: 'select', required: false, options: ['Yes', 'No'] },
          { id: 'entity_mailing_address_line1', label: 'Mailing Address Line 1', type: 'text', required: false },
          { id: 'entity_mailing_address_line2', label: 'Mailing Address Line 2', type: 'text', required: false },
          { id: 'entity_mailing_city', label: 'Mailing City', type: 'text', required: false },
          { id: 'entity_mailing_state', label: 'Mailing State', type: 'select', required: false, additional_info: 'state' },
          { id: 'entity_mailing_zip', label: 'Mailing ZIP Code', type: 'text', required: false },
        ],
        documents: []
      },
      { // main_guarantor_details
        id: 'main_guarantor_details',
        title: 'Main Guarantor Information',
        description: 'Provide the main guarantor’s information.',
        group: 'borrower_information',
        fields: [
          { id: 'entity_participant_first_name', label: 'First Name', type: 'text', required: true },
          { id: 'entity_participant_last_name', label: 'Last Name', type: 'text', required: true },
          { id: 'entity_participant_phone', label: 'Phone Number', type: 'phone', required: true },
          { id: 'entity_participant_date_of_birth', label: 'Date of Birth', type: 'date', required: true },
          { id: 'entity_participant_email', label: 'Email Address', type: 'email', required: true },
          { id: 'entity_participant_employer', label: 'Employer Name', type: 'text', required: false },
          { id: 'entity_participant_position_title', label: 'Employment Position/Title', type: 'text', required: false },
          { id: 'entity_participant_management_rights', label: 'Does this Participant have management rights in the Entity?', type: 'select', required: true, options: YES_NO_OPTIONS_SIMPLE },
          { id: 'entity_participant_stake_in_borrower_percent', label: 'Ownership % in Entity', type: 'percent', required: true },
          { id: 'entity_participant_address_line1', label: 'Present Address Line 1', type: 'text', required: true },
          { id: 'entity_participant_address_line2', label: 'Present Address Line 2', type: 'text', required: false },
          { id: 'entity_participant_city', label: 'Present City', type: 'text', required: true },
          { id: 'entity_participant_state', label: 'Present State', type: 'select', required: true, additional_info: 'state' },
          { id: 'entity_participant_zip', label: 'Present ZIP Code', type: 'text', required: true },
          { id: 'entity_participant_use_different_mailing_address', label: 'Use a different mailing address?', type: 'select', required: false, options: YES_NO_OPTIONS_SIMPLE },
          { id: 'entity_participant_mailing_address_line1', label: 'Mailing Address Line 1', type: 'text', required: false },
          { id: 'entity_participant_mailing_address_line2', label: 'Mailing Address Line 2', type: 'text', required: false },
          { id: 'entity_participant_mailing_city', label: 'Mailing City', type: 'text', required: false },
          { id: 'entity_participant_mailing_state', label: 'Mailing State', type: 'select', required: false, additional_info: 'state' },
          { id: 'entity_participant_mailing_zip', label: 'Mailing ZIP Code', type: 'text', required: false },
          { id: 'preferred_signing_date', label: 'Preferred Signing Date', type: 'date', required: true, additional_info: 'signing_date' },
        ]
      },
      { // entity_participant_details
        id: 'entity_participant_details',
        title: 'Additional Entity Participant Information',
        description: 'List all members, managers, partners, etc. associated with the borrowing entity, including ownership percentages. Information must align with your operating agreement and formation documents.',
        group: 'borrower_information',
        fields: [
          { id: 'entity_participant_first_name', label: 'First Name', type: 'text', required: true },
          { id: 'entity_participant_last_name', label: 'Last Name', type: 'text', required: true },
          { id: 'entity_participant_phone', label: 'Phone Number', type: 'phone', required: true },
          { id: 'entity_participant_date_of_birth', label: 'Date of Birth', type: 'date', required: true },
          { id: 'entity_participant_email', label: 'Email Address', type: 'email', required: true },
          { id: 'entity_participant_employer', label: 'Employer Name', type: 'text', required: false },
          { id: 'entity_participant_position_title', label: 'Employment Position/Title', type: 'text', required: false },
          { id: 'entity_participant_management_rights', label: 'Does this Participant have management rights in the Entity?', type: 'select', required: true, options: YES_NO_OPTIONS_SIMPLE },
          { id: 'entity_participant_stake_in_borrower_percent', label: 'Ownership % in Entity', type: 'percent', required: true },
          { id: 'entity_participant_address_line1', label: 'Present Address Line 1', type: 'text', required: true },
          { id: 'entity_participant_address_line2', label: 'Present Address Line 2', type: 'text', required: false },
          { id: 'entity_participant_city', label: 'Present City', type: 'text', required: true },
          { id: 'entity_participant_state', label: 'Present State', type: 'select', required: true, additional_info: 'state' },
          { id: 'entity_participant_zip', label: 'Present ZIP Code', type: 'text', required: true },
          { id: 'entity_participant_use_different_mailing_address', label: 'Use a different mailing address?', type: 'select', required: false, options: YES_NO_OPTIONS_SIMPLE },
          { id: 'entity_participant_mailing_address_line1', label: 'Mailing Address Line 1', type: 'text', required: false },
          { id: 'entity_participant_mailing_address_line2', label: 'Mailing Address Line 2', type: 'text', required: false },
          { id: 'entity_participant_mailing_city', label: 'Mailing City', type: 'text', required: false },
          { id: 'entity_participant_mailing_state', label: 'Mailing State', type: 'select', required: false, additional_info: 'state' },
          { id: 'entity_participant_mailing_zip', label: 'Mailing ZIP Code', type: 'text', required: false },
        ]
      },
      { // guarantor_information
        id: 'guarantor_information',
        title: 'Guarantor Information',
        description: 'Review the main guarantor and entity participants, then select additional guarantors for this loan.',
        group: 'borrower_information',
        isDynamicallyRenderedSection: true, 
        fields: [],
        documents: []
      },
      { // upload_government_issued_id
        id: 'upload_government_issued_id',
        title: 'Upload Government-Issued ID',
        description: 'Please upload a clear, unexpired government-issued photo ID for each Guarantor and Entity Participant.',
        group: 'borrower_information',
        fields: [],
        documents: [
          { id: 'government_issued_id_doc', name: 'Upload Government-Issued ID', required: true, description: '' },
        ]
      },
      { // upload_entity_formation_document
        id: 'upload_entity_formation_document',
        title: 'Upload Entity Formation Document',
        description: 'Please upload the filed formation document (e.g., Articles of Organization/Incorporation) showing the entity’s legal existence.',
        group: 'borrower_information',
        fields: [],
        documents: [
          { id: 'entity_formation_doc', name: 'Upload Entity Formation Document', required: true, description: '' },
        ]
      },
      { // upload_irs_ein_letter
        id: 'upload_irs_ein_letter',
        title: 'Upload IRS EIN Letter',
        description: 'UPlease upload the IRS-issued EIN confirmation letter for the borrowing entity to verify its federal tax identification number.',
        group: 'borrower_information',
        fields: [],
        documents: [
          { id: 'irs_ein_letter_doc', name: 'Upload IRS EIN Letter', required: true, description: '' },
        ]
      },
      { // upload_operating_agreement
        id: 'upload_operating_agreement',
        title: 'Upload Operating Agreement',
        description: 'Please upload the fully executed operating agreement (or corporate bylaws/partnership agreement, if applicable), including all amendments. Ownership and signing authority must be clearly reflected.',
        group: 'borrower_information',
        fields: [],
        documents: [
          { id: 'operating_agreement_doc', name: 'Upload Operating Agreement', required: true, description: '' },
        ]
      },
      { // upload_certificate_of_good_standing
        id: 'upload_certificate_of_good_standing',
        title: 'Upload Certificate of Good Standing',
        description: 'Please upload a current Certificate of Good Standing from the entity’s state of formation, dated within the last 60 days.',
        group: 'borrower_information',
        fields: [],
        documents: [
          { id: 'certificate_of_good_standing_doc', name: 'Upload Certificate of Good Standing', required: false, description: '' },
        ]
      },
      { // upload_proof_of_liquidity
        id: 'upload_proof_of_liquidity',
        title: 'Upload Proof of Liquidity',
        description: 'Please upload recent bank or brokerage statements (all pages) to verify available funds for down payment, closing costs, reserves, and required project contributions. Large recent deposits may require explanation.',
        group: 'borrower_information',
        fields: [
          { id: 'minimum_available_liquidity', label: 'Minimum Available Liquidity', type: 'currency', required: true },
          { id: 'account_type', label: 'Account Type', type: 'select', required: true, options: LIQUIDITY_ACCOUNT_TYPE_OPTIONS },
        ],
        documents: [
          { id: 'most_recent_bank_statement_doc', name: 'Upload Most Recent Bank Statement', required: true, description: '' },
          { id: 'previous_bank_statement_doc', name: 'Upload Previous Bank Statement (Immediately Before the Most Recent)', required: true, description: '' },
        ]
      }
    ]
  },
  { // miscellaneous
    id: 'miscellaneous',
    title: 'Miscellaneous',
    description: 'Additional documents and information',
    isLevel1Section: true,
    fields: [],
    documents: []
  }
];
